import { generateMeta } from '@forge42/seo-tools/remix/metadata'
import { type Route } from './+types/privacy.ts'

export const meta: Route.MetaFunction = () => {
	// This utility will under the hood generate the twitter & og title and description tags for you.
	const meta = generateMeta(
		{
			title: 'Privacy Policy - DFW Professionals',
			description:
				'How DFW Professionals collects, uses and shares your data, including what happens to the details you submit with a quote request.',
			url: 'https://dfwprofessionals.com/privacy',
		},
		[],
	)
	return meta
}

export default function PrivacyPage() {
	return (
		<>
			<div className="relative py-6">
				<div className="relative container mx-auto max-w-3xl pt-16 pb-12">
					{/* Header */}

					<h1 className="mb-8 text-4xl font-bold tracking-tight sm:text-5xl">
						Privacy Policy
					</h1>

					<div className="mx-auto mt-10 space-y-8">
						<section aria-labelledby="quick-summary">
							<h2 id="quick-summary" className="text-2xl font-semibold">
								Quick Summary
							</h2>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									We only collect the information we need to provide our
									service.
								</li>
								<li>
									We do not sell your details to advertisers or data brokers.
								</li>
								<li>
									If you ask us for quotes, we pass the details you give us to
									businesses in that category so they can contact you. That only
									happens when you tick the box asking us to.
								</li>
								<li>
									Payments are processed securely by Stripe. We never see or
									store your card details.
								</li>
								<li>
									You can request, export, or delete your data at any time.
								</li>
								<li>
									We comply with GDPR (Europe), CCPA (California), and other
									applicable privacy laws.
								</li>
							</ul>
						</section>

						<section aria-labelledby="info-we-collect">
							<h2 id="info-we-collect" className="text-2xl font-semibold">
								1. Information We Collect
							</h2>

							<h3 className="mt-4 text-xl font-medium">
								Information You Provide
							</h3>
							<ul className="list-disc space-y-2 pl-6">
								<li>Account details (name, email address)</li>
								<li>Payment details (processed securely by Stripe)</li>
								<li>Communication preferences</li>
								<li>
									Quote requests: your name, email, optional phone number, the
									work you describe, your timeline, and the page you submitted
									from
								</li>
							</ul>

							<h3 className="mt-6 text-xl font-medium">
								Automatically Collected Information
							</h3>
							<ul className="list-disc space-y-2 pl-6">
								<li>Usage data and analytics</li>
								<li>Device and browser information</li>
								<li>IP address and location data</li>
								<li>Cookies and similar technologies</li>
							</ul>
						</section>

						<section aria-labelledby="how-we-use">
							<h2 id="how-we-use" className="text-2xl font-semibold">
								2. How We Use Your Information
							</h2>
							<p className="mt-4">We use the information we collect to:</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>Provide, maintain, and improve our services</li>
								<li>Process payments and manage subscriptions</li>
								<li>Send important service updates and notifications</li>
								<li>Respond to questions and support requests</li>
								<li>
									Pass quote requests to businesses in the relevant category so
									they can contact you about the work
								</li>
								<li>Ensure security and prevent fraud</li>
								<li>Comply with legal obligations</li>
							</ul>
						</section>

						<section aria-labelledby="quote-requests">
							<h2 id="quote-requests" className="text-2xl font-semibold">
								Quote Requests
							</h2>
							<p className="mt-4">
								If you ask us for quotes, we pass the details you submitted to
								businesses in that category and area so they can contact you
								directly. We do this only when you tick the consent box on the
								form, and only with what you typed into it.
							</p>
							<p className="mt-4">
								We may be paid by those businesses for the introduction. That
								payment never affects our rankings, which are computed from a
								published rubric that money cannot change. See our{' '}
								<a className="underline" href="/scoring-method">
									scoring method
								</a>{' '}
								for how that works.
							</p>
							<p className="mt-4">
								Some privacy laws treat passing personal details to another
								business for payment as a &ldquo;sale&rdquo; or
								&ldquo;sharing&rdquo; of personal information, even where you
								asked us to do it. You can withdraw consent or ask us to delete
								your request at any time by replying to the confirmation email
								or contacting us. Withdrawing consent does not undo an
								introduction already made.
							</p>
						</section>

						<section aria-labelledby="data-sharing">
							<h2 id="data-sharing" className="text-2xl font-semibold">
								3. Data Sharing and Disclosure
							</h2>
							<p className="mt-4">
								We do <strong>not</strong> sell, trade, or rent your personal
								information to advertisers, data brokers, or anyone who wants a
								list. We share data only in these circumstances:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									<strong>Service Providers:</strong> Trusted providers such as
									Stripe (payments)
								</li>
								<li>
									<strong>Legal Requirements:</strong> When required by law or
									to protect rights and safety
								</li>
								<li>
									<strong>Business Transfers:</strong> As part of a merger,
									acquisition, or sale of assets
								</li>
								<li>
									<strong>Quote Requests:</strong> When you submit a quote
									request and tick the consent box, we pass the details in that
									request to businesses in the relevant category and area so
									they can contact you about the work. We may be paid by those
									businesses for the introduction. We do not pass your details
									to anyone who did not ask to receive requests in that
									category, and we do not share your browsing history or
									anything you did not put in the form.
								</li>
								<li>
									<strong>With Your Consent:</strong> If you explicitly
									authorize us
								</li>
							</ul>
						</section>

						<section aria-labelledby="google-oauth">
							<h2 id="google-oauth" className="text-2xl font-semibold">
								4. Analytics Data
							</h2>

							<h3 className="mt-6 text-xl font-medium">
								Analytics Limited Use
							</h3>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									We comply with PostHog user data and developer policies.
								</li>
								<li>
									We do not use, transfer, or sell PostHog data to train machine
									learning or AI models.
								</li>
								<li>
									Aggregated/anonymized data is used only to provide analytics
									to you.
								</li>
							</ul>
						</section>

						<section aria-labelledby="payments">
							<h2 id="payments" className="text-2xl font-semibold">
								5. Payment Processing (Stripe)
							</h2>
							<ul className="mt-4 list-disc space-y-2 pl-6">
								<li>
									Payments are handled by Stripe, a PCI DSS compliant processor.
								</li>
								<li>Stripe processes your payment details directly.</li>
								<li>We do not store credit card numbers.</li>
								<li>
									We only receive confirmation of payment and basic billing
									info.
								</li>
								<li>
									Stripe’s{' '}
									<a
										href="https://stripe.com/privacy"
										target="_blank"
										rel="noopener noreferrer"
										className="underline"
									>
										Privacy Policy
									</a>{' '}
									governs their handling of your data.
								</li>
							</ul>
						</section>

						<section aria-labelledby="security">
							<h2 id="security" className="text-2xl font-semibold">
								6. Data Security
							</h2>
							<p className="mt-4">
								We use industry-standard practices to protect your data:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>End-to-end encryption (TLS/SSL)</li>
								<li>Encryption at rest for stored data</li>
								<li>Regular security audits and patching</li>
								<li>Access controls and multi-factor authentication</li>
								<li>Secure development practices</li>
							</ul>
						</section>

						<section aria-labelledby="retention">
							<h2 id="retention" className="text-2xl font-semibold">
								7. Data Retention
							</h2>
							<p className="mt-4">We keep data only as long as necessary:</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									Account data: Until you delete your account or request
									deletion
								</li>
								<li>
									Payment/billing records: As required for accounting and legal
									purposes
								</li>
								<li>
									Logs/analytics: Up to 12 months for service improvement and
									security
								</li>
							</ul>
						</section>

						<section aria-labelledby="rights">
							<h2 id="rights" className="text-2xl font-semibold">
								8. Your Rights and Choices
							</h2>
							<p className="mt-4">You have the right to:</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>
									<strong>Access</strong>: Request a copy of your data
								</li>
								<li>
									<strong>Correction</strong>: Update inaccurate info
								</li>
								<li>
									<strong>Deletion</strong>: Request account/data deletion
								</li>
								<li>
									<strong>Portability</strong>: Transfer data to another service
								</li>
								<li>
									<strong>Opt-Out</strong>: Unsubscribe from marketing
									communications
								</li>
								<li>
									<strong>Revoke Access</strong>: Disconnect Google accounts
									anytime
								</li>
							</ul>
							<p className="mt-4">
								<strong>For EU/UK Residents (GDPR):</strong> You may have
								additional rights, including the right to restrict processing
								and to lodge a complaint with your data protection authority.
							</p>
							<p className="mt-2">
								<strong>For California Residents (CCPA):</strong> You have the
								right to know, delete, and opt-out of certain data uses.
							</p>
						</section>

						<section aria-labelledby="cookies">
							<h2 id="cookies" className="text-2xl font-semibold">
								9. Cookies and Tracking
							</h2>
							<p className="mt-4">
								We use cookies and similar technologies to:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>Maintain session and login status</li>
								<li>Remember user preferences</li>
								<li>Analyze service usage and performance</li>
								<li>Provide personalized features</li>
							</ul>
							<p className="mt-4">
								<strong>EU/UK users:</strong> A cookie consent banner allows you
								to manage preferences in compliance with GDPR.
							</p>
						</section>

						<section aria-labelledby="children">
							<h2 id="children" className="text-2xl font-semibold">
								10. Children’s Privacy
							</h2>
							<p className="mt-4">
								Our services are not directed to children under 13. We do not
								knowingly collect personal data from children. If we become
								aware of such data, we will delete it immediately.
							</p>
						</section>

						<section aria-labelledby="changes">
							<h2 id="changes" className="text-2xl font-semibold">
								11. Changes to This Policy
							</h2>
							<p className="mt-4">
								We may update this policy from time to time. If we make material
								changes, we will:
							</p>
							<ul className="list-disc space-y-2 pl-6">
								<li>Email you at your registered address, or</li>
								<li>Post a notice on our website</li>
							</ul>
							<p className="mt-4">
								Your continued use of our service after changes take effect
								constitutes acceptance of the updated policy.
							</p>
						</section>

						<section aria-labelledby="contact">
							<h2 id="contact" className="text-2xl font-semibold">
								12. Contact Us
							</h2>
							<p className="mt-4">
								For any questions, concerns, or data subject requests:
							</p>
							<p className="mt-2">
								Email:{' '}
								<a
									href="mailto:info@dfwprofessionals.com"
									className="underline"
								>
									info@dfwprofessionals.com
								</a>
							</p>
						</section>
					</div>
				</div>
			</div>
		</>
	)
}
